import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface SyncDashboardData {
  operations_by_status: {
    accepted: number;
    conflict: number;
    error: number;
    already_processed: number;
  };
  unresolved_conflicts: number;
  devices: DeviceInfo[];
  dead_letter_queue: DeadLetterItem[];
}

export interface DeviceInfo {
  id: string;
  shop_id: string;
  device_id: string;
  last_sync_id: string | null;
  last_sync_at: string | null;
  shop?: { id: string; name: string } | null;
}

export interface DeadLetterItem {
  id: string;
  shop_id: string;
  device_id: string;
  entity_type: string;
  entity_id: string;
  operation: string;
  status: string;
  conflict_details: string | null;
  created_at: string;
}

export interface SyncOperationItem {
  id: string;
  shop_id: string;
  device_id: string;
  entity_type: string;
  entity_id: string;
  operation: string;
  status: string;
  payload: string | null;
  conflict_details: string | null;
  created_at: string;
}

interface PaginatorPayload<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface NormalizedPaginated<T> {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export function useAdminSync() {
  return useQuery({
    queryKey: ['admin', 'sync'],
    queryFn: async () => {
      const response = await api.get<SyncDashboardData>('/admin/sync');
      return response.data;
    },
  });
}

export function useAdminSyncOperations(
  params: {
    entity_type?: string;
    status?: string;
    device_id?: string;
    page?: number;
  } = {},
) {
  const searchParams = new URLSearchParams();
  if (params.entity_type) searchParams.set('entity_type', params.entity_type);
  if (params.status) searchParams.set('status', params.status);
  if (params.device_id) searchParams.set('device_id', params.device_id);
  if (params.page) searchParams.set('page', params.page.toString());
  const qs = searchParams.toString();

  return useQuery({
    queryKey: ['admin', 'sync', 'operations', params],
    queryFn: async () => {
      const response = await api.get<PaginatorPayload<SyncOperationItem>>(
        `/admin/sync/operations${qs ? `?${qs}` : ''}`,
      );
      const payload = response.data;

      return {
        data: Array.isArray(payload?.data) ? payload.data : [],
        meta: {
          current_page: payload?.current_page ?? 1,
          last_page: payload?.last_page ?? 1,
          per_page: payload?.per_page ?? 15,
          total: payload?.total ?? 0,
        },
      } satisfies NormalizedPaginated<SyncOperationItem>;
    },
  });
}
